import { useCallback } from 'react';
import { events } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { buildAnalyticsEventPayload } from 'utils/analyticsEventContext';
import { isEmbeddedMeetingPanelPath } from 'utils/meetingPanelRoutes';

export const EVENT_TYPES = [
  'login',
  'logout',
  'page_view',
  'button_click',
  'password_changed',
  'popup',
  'enter_flow',
  'flow_step_view',
  'flow_continue',
  'flow_previous',
  'flow_submit',
  'flow_heartbeat',
  'flow_visibility_changed',
  'flow_resume',
  'flow_exit',
  'course_complete',
  'toolkit_note_click',
  'toolkit_note_create',
  'toolkit_note_close',
  'certificate_linkedin_connect_click',
  'certificate_linkedin_post_click',
  'certificate_linkedin_post_success',
  'certificate_linkedin_post_failure',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

const recentEventTimestamps = new Map<string, number>();

const EVENT_DEDUPE_WINDOWS_MS: Partial<Record<EventType, number>> = {
  page_view: 5_000,
  enter_flow: 10_000,
  flow_heartbeat: 14_000,
  flow_visibility_changed: 2_000,
  flow_resume: 5_000,
  flow_exit: 5_000,
};

const buildEventSignature = (eventType: EventType, eventData: Record<string, any>): string | null => {
  switch (eventType) {
    case 'page_view':
      return `${eventType}:${window.location.pathname}`;
    case 'enter_flow':
      return `${eventType}:${eventData.flowRunId ?? 'none'}:${eventData.flowId ?? 'none'}`;
    case 'flow_heartbeat':
      return `${eventType}:${eventData.flowRunId ?? 'none'}:${eventData.flowId ?? 'none'}`;
    case 'flow_visibility_changed':
      return `${eventType}:${eventData.flowRunId ?? 'none'}:${eventData.visibilityState ?? 'unknown'}`;
    case 'flow_resume':
      return `${eventType}:${eventData.flowRunId ?? 'none'}:${eventData.reason ?? 'unknown'}`;
    case 'flow_exit':
      return `${eventType}:${eventData.flowRunId ?? 'none'}:${eventData.reason ?? 'unknown'}`;
    default:
      return null;
  }
};

const shouldSendEvent = (eventType: EventType, eventData: Record<string, any>): boolean => {
  const dedupeWindowMs = EVENT_DEDUPE_WINDOWS_MS[eventType];
  if (!dedupeWindowMs) {
    return true;
  }

  const signature = buildEventSignature(eventType, eventData);
  if (!signature) {
    return true;
  }

  const now = Date.now();
  const lastSentAt = recentEventTimestamps.get(signature) ?? 0;
  if (now - lastSentAt < dedupeWindowMs) {
    return false;
  }

  recentEventTimestamps.set(signature, now);

  if (recentEventTimestamps.size > 200) {
    Array.from(recentEventTimestamps.entries()).forEach(([key, timestamp]) => {
      if (now - timestamp > 60_000) {
        recentEventTimestamps.delete(key);
      }
    });
  }

  return true;
};

/**
 * Hook for tracking user events throughout the application
 * 
 * @returns Object with track function
 */
export const useEventTracking = () => {
  const { user, isAuthenticated } = useAuth();

  /**
   * Track a user event
   * 
   * @param eventType - Type of event (e.g., 'page_view', 'button_click')
   * @param eventData - Additional data to store with the event
   */
  const track = useCallback((eventType: EventType, eventData: Record<string, any> = {}) => {
    if (isEmbeddedMeetingPanelPath()) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    if (!shouldSendEvent(eventType, eventData)) {
      return;
    }

    const enhancedData = buildAnalyticsEventPayload(eventData, user);

    // Fire and forget - don't wait for response
    const maybePromise = events.track(eventType, enhancedData);

    if (typeof (maybePromise as Promise<unknown>)?.catch === 'function') {
      maybePromise.catch(error => {
        // Just log errors, don't interrupt user experience
        console.error('Error tracking event:', error);
      });
    }
  }, [isAuthenticated, user]);

  return { track };
};
